export const VOICE_LANGUAGES = [
  {
    code: 'en', label: 'English', dialects: [
      { locale: 'en-US', label: 'United States' },
      { locale: 'en-GB', label: 'United Kingdom' },
      { locale: 'en-AU', label: 'Australia' },
    ],
  },
  {
    code: 'ar', label: 'العربية — Arabic', dialects: [
      { locale: 'ar-EG', label: 'المصرية — Egypt' },
      { locale: 'ar-KW', label: 'الكويتية — Kuwait' },
      { locale: 'ar-SA', label: 'السعودية — Saudi Arabia' },
      { locale: 'ar-AE', label: 'الإماراتية — United Arab Emirates' },
      { locale: 'ar-QA', label: 'القطرية — Qatar' },
      { locale: 'ar-BH', label: 'البحرينية — Bahrain' },
      { locale: 'ar-OM', label: 'العمانية — Oman' },
      { locale: 'ar-JO', label: 'الأردنية — Jordan' },
      { locale: 'ar-LB', label: 'اللبنانية — Lebanon' },
      { locale: 'ar-SY', label: 'السورية — Syria' },
      { locale: 'ar-IQ', label: 'العراقية — Iraq' },
      { locale: 'ar-PS', label: 'الفلسطينية — Palestine' },
      { locale: 'ar-MA', label: 'المغربية — Morocco' },
      { locale: 'ar-DZ', label: 'الجزائرية — Algeria' },
      { locale: 'ar-TN', label: 'التونسية — Tunisia' },
      { locale: 'ar-YE', label: 'اليمنية — Yemen' },
    ],
  },
] as const

type VoiceLocale = typeof VOICE_LANGUAGES[number]['dialects'][number]['locale']
export type VoicePreference = 'browser' | VoiceLocale

const locales = new Set<string>(VOICE_LANGUAGES.flatMap((language) => language.dialects.map(({ locale }) => locale)))
const storageKey = (userId: number) => `taskly:voice:locale:v1:${userId}`

function isVoicePreference(value: string | null): value is VoicePreference {
  return value === 'browser' || (value !== null && locales.has(value))
}

export function readVoicePreference(userId: number | undefined): VoicePreference {
  if (userId === undefined) return 'browser'
  try {
    const value = window.localStorage.getItem(storageKey(userId))
    return isVoicePreference(value) ? value : 'browser'
  } catch {
    return 'browser'
  }
}

export function saveVoicePreference(userId: number, value: VoicePreference): boolean {
  if (!isVoicePreference(value)) return false
  try {
    window.localStorage.setItem(storageKey(userId), value)
    return true
  } catch {
    return false
  }
}

/** Read when recording starts so every field and tab uses the latest saved choice. */
export function getVoiceLocale(userId: number | undefined): string {
  const preference = readVoicePreference(userId)
  return preference === 'browser' ? navigator.language || 'en-US' : preference
}

/**
 * Which engine the microphone uses.
 *
 *  - `whisper`: record the clip and let the server transcribe it with the
 *    bundled Whisper model. Works in every browser with a microphone, handles
 *    Arabic dialects well, and no audio goes to a third party. Offered only
 *    when the deployment has the engine set up (session `voice_transcription`).
 *  - `browser`: the browser's own live speech recognition, word by word.
 */
export type VoiceEngine = 'whisper' | 'browser'

const engineKey = (userId: number) => `taskly:voice:engine:v1:${userId}`

export function readVoiceEngine(userId: number | undefined): VoiceEngine {
  if (userId === undefined) return 'whisper'
  try {
    const value = window.localStorage.getItem(engineKey(userId))
    return value === 'browser' ? 'browser' : 'whisper'
  } catch {
    return 'whisper'
  }
}

export function saveVoiceEngine(userId: number, engine: VoiceEngine): boolean {
  try {
    window.localStorage.setItem(engineKey(userId), engine)
    return true
  } catch {
    return false
  }
}

/** The language hint the server engine accepts: auto, ar or en. */
export function getWhisperLanguage(userId: number | undefined): 'auto' | 'ar' | 'en' {
  const preference = readVoicePreference(userId)
  if (preference === 'browser') return 'auto'
  const code = preference.slice(0, 2)
  return code === 'ar' || code === 'en' ? code : 'auto'
}
